// TODO: Move this functionality out of `webapp` to a package of its own.
import { StrictMode, Suspense } from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import { QueryClientProvider } from 'react-query';

import { HotsauceProvider } from '@hotjar/hotsauce';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { onNavigate } from '@hotjar/webapp/utils/routing/on-navigate';
import { BrowserRouter } from '@hotjar/webapp-routing';
import { DataPrefetcher } from '@hotjar/webapp/components/DataPrefetcher';
import { useTranslation } from '@hotjar/translations';

export interface GlobalModalProps<TSubmit, TCancel = Error> {
  onSubmit(data?: TSubmit): void;
  onCancel(error?: TCancel): void;
}

function Providers({ children }: { children: JSX.Element }) {
  const { i18n, ready } = useTranslation();

  return (
    <HotsauceProvider
      i18n={{
        strings: ready ? i18n.getResourceBundle(i18n.language, 'hotsauce') : {},
      }}
      onNavigate={onNavigate}
    >
      <QueryClientProvider client={queryClient}>
        {/* We use a custom history object here so that we can access it from the Angular app to allow
         navigation between the react and angular apps. */}
        <BrowserRouter>
          <DataPrefetcher>
            <Suspense fallback={null}>{children}</Suspense>
          </DataPrefetcher>
        </BrowserRouter>
      </QueryClientProvider>
    </HotsauceProvider>
  );
}

/**
 * Future person, this function has been re-exported by:
 *  - packages/subscription-management
 *  - packages/service-management
 *  - packages/involuntary-churn
 *  - packages/voluntary-churn
 * If you extract this out of webapp into a standalone util please inform the FE teams, so they are aware:
 *  - `@hotjar/monetization-platform` or `@hotjar/billing`
 *  - `@hotjar/re-engagement`
 */
export function createGlobalModal<TProps, TSubmit, TCancel = Error, TOptions = void>(
  Modal: (props: TProps & GlobalModalProps<TSubmit, TCancel> & TOptions) => JSX.Element | null,
): (props?: TProps) => Promise<TSubmit> {
  const root = document.getElementById('root');
  const container = document.createElement('div');

  const destroyContainer = () => {
    unmountComponentAtNode(container);
    root?.removeChild(container);
  };

  return (props?: TProps): Promise<TSubmit> => {
    return new Promise<TSubmit>((resolve, reject) => {
      const onSubmit = (data: TSubmit) => {
        resolve(data);
        destroyContainer();
      };
      const onCancel = (error: TCancel) => {
        reject(error);
        destroyContainer();
      };

      root?.appendChild(container);

      render(
        <StrictMode>
          <Providers>
            <Modal {...(props as TProps & TOptions)} onCancel={onCancel} onSubmit={onSubmit} />
          </Providers>
        </StrictMode>,
        container,
      );
    });
  };
}
